import { useEffect } from 'react'

import { ROUTE_EPICS } from '../routeEpics'

let activeComponent
let activeKeys = []
let activeDependencies = []
let pendingTeardown = null
// The route whose wrapper last committed. activeComponent can be ahead of it:
// a route renders (and takes over the data layer) before it commits, and a
// lazy page that suspends never commits at all.
let committedComponent
// Slice keys this module injected: the active route's, plus the previous
// route's that wait for its tree to unmount.
let activeReducerKeys = []
let staleReducerKeys = []

const sameDependencies = (applied, next) =>
  applied.length === next.length && applied.every((key, index) => key === next[index])

/**
 * Injects the route's data layer. Idempotent when this Component is already
 * active with the same dependency list. A different list re-applies reducers
 * and epics; identity alone is not enough, because one wrapper is shared for
 * every registration of that Component.
 */
const applyDataLayer = (store, Component, dataLayerDependencies) => {
  if (
    activeComponent === Component &&
    sameDependencies(activeDependencies, dataLayerDependencies)
  ) {
    return
  }

  // 1. Module evaluation first: the one step here that can throw or
  //    dispatch. The latch is only set once this succeeds, so a throwing
  //    chunk leaves the registry untouched and the next render for this
  //    Component retries injection instead of short-circuiting on the
  //    check above.
  const reducers = {}
  dataLayerDependencies.forEach((dependency) => {
    const nextReducer = require(`../containers/${dependency}/reducer`).default
    reducers[dependency.toLowerCase()] = nextReducer()(undefined, {})
  })
  activeComponent = Component
  activeDependencies = dataLayerDependencies.slice()

  // 2. Inject next - prev, before any of next's epics can start.
  const nextReducerKeys = Object.keys(reducers)
  const ownedReducerKeys = [...activeReducerKeys, ...staleReducerKeys]
  store.injectReducers(
    Object.fromEntries(Object.entries(reducers).filter(([key]) => !ownedReducerKeys.includes(key)))
  )
  // prev - next is only removed once the previous route's tree has unmounted
  // (flushStaleReducers): this render runs while that tree is still mounted
  // and subscribed, and its selectors would read a slice that is gone
  // (IC-52036).
  staleReducerKeys = ownedReducerKeys.filter((key) => !nextReducerKeys.includes(key))
  activeReducerKeys = nextReducerKeys

  // 3. Eject prev - next, STRICTLY before the slice disappears — the
  //    window between "slice removed" and "epics stopped" is exactly
  //    where an epic can read a slice that no longer exists (IC-39093).
  //    Inject next - prev. `filter(k => k in ROUTE_EPICS)` is what lets
  //    a dependency with an inert reducer entry (e.g. SpearPhishingAgent,
  //    whose epics are always-on rather than route-scoped) stay in
  //    dataLayerDependencies harmlessly — it's simply not a registry key.
  const nextKeys = dataLayerDependencies.filter((k) => k in ROUTE_EPICS)
  activeKeys.filter((k) => !nextKeys.includes(k)).forEach(store.ejectEpic)
  nextKeys
    .filter((k) => !activeKeys.includes(k))
    .forEach((k) => store.injectEpic(k, ROUTE_EPICS[k]))
  activeKeys = nextKeys
}

// After commit, the tree the stale slices belonged to is gone: React runs a
// deleted tree's effect cleanups (its store unsubscriptions) before the new
// tree's effects.
const flushStaleReducers = (store) => {
  if (!staleReducerKeys.length) return
  const keys = staleReducerKeys
  staleReducerKeys = []
  store.removeReducers(keys)
}

/**
 * Tears the data layer down one microtask after the committed route's wrapper
 * unmounts, for when no other data-layer route commits in its place (/kpi,
 * the SOC agent dashboard, restricted access). That includes a route that
 * rendered and took over but never committed (its lazy page still loading
 * when the user moved on): its epics and slices go too.
 */
const scheduleTeardown = (store, Component) => {
  if (committedComponent !== Component) return
  pendingTeardown = Component
  // Deferred, not immediate: StrictMode tears an effect down and sets it
  // back up synchronously, and an immediate eject would stop an epic whose
  // own route is about to re-subscribe to it. The re-mount clears
  // `pendingTeardown` before this microtask runs.
  queueMicrotask(() => {
    if (pendingTeardown !== Component) return
    pendingTeardown = null
    // A successor committed in the same commit (its effects ran before this
    // microtask) and owns the data layer now.
    if (committedComponent !== Component) return

    // Epics before slices, for the same reason as step 3 above.
    activeKeys.forEach(store.ejectEpic)
    activeKeys = []
    activeDependencies = []
    activeComponent = undefined
    committedComponent = undefined
    // The slice goes with the epics. Keeping it would preserve whatever
    // `loader: true` the ejected epic never got to settle, and the next
    // visit to the route would read that stale state instead of a fresh
    // initial one. This is what applyDataLayer already does when route B
    // takes over from A; here there is no B.
    const keys = [...activeReducerKeys, ...staleReducerKeys]
    activeReducerKeys = []
    staleReducerKeys = []
    store.removeReducers(keys)
  })
}

// One wrapper per routed Component, so `<Route component={...}>` keeps a
// stable element type across re-renders of the router host instead of
// remounting the whole page.
const wrappers = new WeakMap()

export const renderComponentWithDataLayer =
  (store) =>
  (Component, dataLayerDependencies, componentProps = {}) => {
    let entry = wrappers.get(Component)

    if (!entry) {
      entry = {}

      const Wrapper = (props) => {
        // In the render body, not an effect: the routed component is this
        // wrapper's CHILD, and child effects run before the parent's — an
        // effect here would land after the page already dispatched fetches
        // into a store missing the slice/epic.
        applyDataLayer(entry.store, Component, entry.dataLayerDependencies)

        useEffect(() => {
          committedComponent = Component
          flushStaleReducers(entry.store)
        })

        useEffect(() => {
          // Cancels a teardown scheduled by StrictMode's synchronous cleanup+setup.
          if (pendingTeardown === Component) pendingTeardown = null
          return () => scheduleTeardown(entry.store, Component)
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])

        return <Component {...props} {...entry.componentProps} />
      }

      Wrapper.displayName = `WithDataLayer(${Component.displayName || Component.name || 'Route'})`
      entry.Wrapper = Wrapper
      wrappers.set(Component, entry)
    } else if (
      NODE_ENV !== 'production' &&
      String(entry.dataLayerDependencies) !== String(dataLayerDependencies)
    ) {
      // One wrapper per Component. A different list is applied on the next
      // render of that wrapper, which ejects and injects again. Alternating
      // lists on one component identity swap the data layer every time.
      // eslint-disable-next-line no-console
      console.warn(
        `[asyncReducer] ${entry.Wrapper.displayName} re-registered with different ` +
          `dataLayerDependencies: [${entry.dataLayerDependencies}] -> [${dataLayerDependencies}]`
      )
    }

    entry.store = store
    entry.dataLayerDependencies = dataLayerDependencies
    entry.componentProps = componentProps

    return entry.Wrapper
  }

/**
 * Clears the module-level route state so tests cannot leak into each other.
 * Does not touch `wrappers`: wrapper identity is keyed by Component and is
 * meant to survive across tests/routes, not just resets.
 */
// eslint-disable-next-line no-underscore-dangle, camelcase
export const __resetDataLayerForTests = () => {
  activeComponent = undefined
  activeKeys = []
  activeDependencies = []
  pendingTeardown = null
  committedComponent = undefined
  activeReducerKeys = []
  staleReducerKeys = []
}
